import React, { useState } from "react"
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Info, Payment, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { useConfiguracaoFormasDePagamentoContext } from "../context/useConfiguracaoFormasDePagamentoContext"
import { ConfiguracaoFormasDePagamentoActionsTypes } from "../context/configuracaoFormasDePagamentoActions"
import type { SalvarFormasPagamentoResposta } from "../../../services/configuracaoFormasPagamento"
import type { OrigemPedidoAceita } from "../models/configuracaoFormasDePagamentoModel"

const ID_PAGSEGURO = 71
const ID_GETNET = 91

const TAXA_INFO: Record<number, string> = {
    [ID_PAGSEGURO]:
        "Para o correto funcionamento desta forma de pagamento, deve ser ativada a integração com o PagSeguro em Cadastro > Parâmetros > Parâmetros de Integração",
    [ID_GETNET]:
        "Para o correto funcionamento desta forma de pagamento, deve ser ativada a integração com a GetNet em Cadastro > Parâmetros > Parâmetros de Integração",
}

const OBSERVACAO: Record<number, string> = {
    [ID_PAGSEGURO]: " (PAGSEGURO)",
    [ID_GETNET]: " (GETNET)",
}

// Valor vazio grava null no backend, que significa aceitar em todas as origens.
const OPCOES_ORIGEM_PEDIDO: { valor: Exclude<OrigemPedidoAceita, null> | ""; label: string }[] = [
    { valor: "", label: "Todos" },
    { valor: "CLIENTE_DELIVERY", label: "Apenas delivery" },
    { valor: "CLIENTE_RETIRADA", label: "Apenas retirada" },
    { valor: "CLIENTE_MESA", label: "Apenas mesa" },
    { valor: "CLIENTE", label: "Todos os pedidos do cliente" },
    { valor: "REGISTRADOR", label: "Apenas registrador" },
]

const ORIGEM_PEDIDO_INFO =
    "Limita de onde a forma pode ser usada. Apenas delivery, Apenas retirada e Apenas mesa valem " +
    "para os pedidos que o próprio cliente faz; Todos os pedidos do cliente libera os três. Um " +
    "pedido lançado no registrador conta como registrador, mesmo que seja para entrega ou mesa. " +
    "Delivery e retirada só são aplicados na finalização do pedido: o aplicativo ainda mostra a " +
    "forma no cardápio, porque nessa hora o cliente ainda não escolheu entre entrega e retirada."

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "100%",
        width: "100%",
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    descricao: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(3),
        fontSize: "0.95rem",
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15 },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    // Switch "Aceitar" em VERDE quando ligado. Aplicado via `classes` do Switch (não por
    // className com seletor global): neste setup do MUI v4 as classes de slot vêm com
    // sufixo numérico (ex.: MuiSwitch-switchBase-790), então `.MuiSwitch-switchBase` não
    // casa. Usando as classes proprias + refs JSS ($checked/$track) o override é robusto.
    switchAceitarBase: {
        "&$switchAceitarChecked": { color: "#4caf50" },
        "&$switchAceitarChecked + $switchAceitarTrack": { backgroundColor: "#4caf50" },
    },
    switchAceitarChecked: {},
    switchAceitarTrack: {},
    cardContent: {
        padding: theme.spacing(2),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    pixFields: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        padding: "10px 32px",
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#43a047" },
        [theme.breakpoints.down("sm")]: { width: "100%" },
    },
    erroBox: {
        padding: theme.spacing(2),
        backgroundColor: "#fff3cd",
        borderRadius: 8,
        marginBottom: theme.spacing(2),
        color: "#856404",
    },
    avisoBox: {
        padding: theme.spacing(2),
        backgroundColor: "#d1ecf1",
        borderRadius: 8,
        marginBottom: theme.spacing(2),
        color: "#0c5460",
    },
}))

export function FormasDePagamentoForm() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const { dados, dispatch, erroCarregamento, salvar } = useConfiguracaoFormasDePagamentoContext()
    const [avisos, setAvisos] = useState<string[]>([])

    const handleSalvar = async () => {
        try {
            const resultado = (await salvar()) as SalvarFormasPagamentoResposta | undefined
            if (resultado?.avisos && resultado.avisos.length > 0) {
                setAvisos(resultado.avisos)
            } else {
                setAvisos([])
            }
            enqueueSnackbar("Configuração atualizada com sucesso!", { variant: "success" })
        } catch (err) {
            enqueueSnackbar((err as Error).message || "Erro ao salvar", { variant: "error" })
        }
    }

    const toggleAssociado = (index: number) => {
        dispatch({
            type: ConfiguracaoFormasDePagamentoActionsTypes.TOGGLE_ASSOCIADO,
            payload: { index },
        })
    }

    const atualizarCampo = (index: number, campo: string, valor: string | number | null) => {
        dispatch({
            type: ConfiguracaoFormasDePagamentoActionsTypes.ATUALIZAR_CAMPO,
            payload: { index, campo: campo as never, valor },
        })
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box className={classes.header}>
                    <Box className={classes.headerLeft}>
                        <Payment style={{ color: "#4caf50", fontSize: 36 }} />
                        <Typography variant="h4" className={classes.titleText}>
                            Formas de Pagamento
                        </Typography>
                    </Box>
                </Box>

                <Typography className={classes.descricao}>
                    Selecione as formas de pagamento aceitas no seu delivery. Você pode definir uma
                    porcentagem de desconto para cada forma — por exemplo, preencha 10 para aplicar
                    10% de desconto.
                </Typography>

                {erroCarregamento && (
                    <Box className={classes.erroBox}>
                        <Typography variant="body2">{erroCarregamento}</Typography>
                    </Box>
                )}

                {avisos.length > 0 && (
                    <Box className={classes.avisoBox}>
                        {avisos.map((aviso, i) => (
                            <Typography key={i} variant="body2">
                                {aviso}
                            </Typography>
                        ))}
                    </Box>
                )}

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Payment className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Formas de Pagamento
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ minWidth: 860, width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 90 }}>
                                            Posição
                                        </TableCell>
                                        <TableCell style={{ minWidth: 70 }} align="center">
                                            Aceitar
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220 }}>
                                            Nome
                                        </TableCell>
                                        <TableCell style={{ minWidth: 100 }}>
                                            Desconto %
                                        </TableCell>
                                        <TableCell style={{ minWidth: 160 }}>
                                            Origem do pedido
                                            <Tooltip title={ORIGEM_PEDIDO_INFO}>
                                                <Info
                                                    style={{
                                                        fontSize: "0.875rem",
                                                        marginLeft: 4,
                                                        verticalAlign: "text-top",
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 110 }}>
                                            Código PDV
                                        </TableCell>
                                        <TableCell style={{ minWidth: 110 }}>
                                            Tipo PDV
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.formasPagamento.map((forma, index) => (
                                        <React.Fragment key={forma.idFormaPagamento}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        type="number"
                                                        value={forma.ordemExib ?? ""}
                                                        onChange={e =>
                                                            atualizarCampo(
                                                                index,
                                                                "ordemExib",
                                                                e.target.value
                                                                    ? parseInt(e.target.value)
                                                                    : null
                                                            )
                                                        }
                                                        inputProps={{ min: 0, style: { padding: "6px 8px" } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Switch
                                                        checked={forma.associado}
                                                        onChange={() => toggleAssociado(index)}
                                                        classes={{
                                                            switchBase: classes.switchAceitarBase,
                                                            checked: classes.switchAceitarChecked,
                                                            track: classes.switchAceitarTrack,
                                                        }}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 4,
                                                        }}
                                                    >
                                                        <span title={forma.descricaoFormaPagamento}>
                                                            {forma.descricaoFormaPagamento}
                                                            {OBSERVACAO[forma.idFormaPagamento] ?? ""}
                                                        </span>
                                                        {TAXA_INFO[forma.idFormaPagamento] && (
                                                            <Tooltip
                                                                title={TAXA_INFO[forma.idFormaPagamento]}
                                                                placement="top"
                                                            >
                                                                <Info
                                                                    style={{
                                                                        fontSize: 16,
                                                                        color: "#7f8c8d",
                                                                        flexShrink: 0,
                                                                        cursor: "help",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    {forma.associado && (
                                                        <TextField
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            label="Nome exibido ao cliente"
                                                            placeholder={
                                                                forma.descricaoPersonalizadaSugerida ||
                                                                forma.descricaoPadrao ||
                                                                forma.descricaoFormaPagamento
                                                            }
                                                            value={forma.descricaoPersonalizada ?? ""}
                                                            onChange={e =>
                                                                atualizarCampo(
                                                                    index,
                                                                    "descricaoPersonalizada",
                                                                    e.target.value || null
                                                                )
                                                            }
                                                            helperText="Em branco: usa o nome sugerido/padrão"
                                                            inputProps={{ maxLength: 100 }}
                                                            style={{ marginTop: 8 }}
                                                        />
                                                    )}
                                                    {forma.pix && forma.associado && (
                                                        <Box className={classes.pixFields}>
                                                            <TextField
                                                                variant="outlined"
                                                                size="small"
                                                                fullWidth
                                                                label="Tipo da chave (ex: cnpj, telefone)"
                                                                value={forma.tipoChavePix ?? ""}
                                                                onChange={e =>
                                                                    atualizarCampo(
                                                                        index,
                                                                        "tipoChavePix",
                                                                        e.target.value || null
                                                                    )
                                                                }
                                                            />
                                                            <TextField
                                                                variant="outlined"
                                                                size="small"
                                                                fullWidth
                                                                label="Chave PIX"
                                                                value={forma.chavePix ?? ""}
                                                                onChange={e =>
                                                                    atualizarCampo(
                                                                        index,
                                                                        "chavePix",
                                                                        e.target.value || null
                                                                    )
                                                                }
                                                            />
                                                            <TextField
                                                                variant="outlined"
                                                                size="small"
                                                                fullWidth
                                                                label="Código QR Code"
                                                                value={forma.codigoQrCodePix ?? ""}
                                                                onChange={e =>
                                                                    atualizarCampo(
                                                                        index,
                                                                        "codigoQrCodePix",
                                                                        e.target.value || null
                                                                    )
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        type="number"
                                                        value={forma.desconto ?? ""}
                                                        onChange={e =>
                                                            atualizarCampo(
                                                                index,
                                                                "desconto",
                                                                e.target.value
                                                                    ? parseFloat(e.target.value)
                                                                    : null
                                                            )
                                                        }
                                                        inputProps={{
                                                            min: -100,
                                                            max: 100,
                                                            step: 0.01,
                                                            style: { padding: "6px 8px" },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={forma.origemPedidoAceita ?? ""}
                                                        onChange={e =>
                                                            atualizarCampo(
                                                                index,
                                                                "origemPedidoAceita",
                                                                (e.target.value as OrigemPedidoAceita) || null
                                                            )
                                                        }
                                                        SelectProps={{
                                                            style: { fontSize: "0.8125rem" },
                                                        }}
                                                    >
                                                        {OPCOES_ORIGEM_PEDIDO.map(opcao => (
                                                            <MenuItem key={opcao.label} value={opcao.valor}>
                                                                {opcao.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={forma.codigoPdv ?? ""}
                                                        onChange={e =>
                                                            atualizarCampo(
                                                                index,
                                                                "codigoPdv",
                                                                e.target.value || null
                                                            )
                                                        }
                                                        inputProps={{ style: { padding: "6px 8px" } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={forma.tipoPdv ?? ""}
                                                        onChange={e =>
                                                            atualizarCampo(
                                                                index,
                                                                "tipoPdv",
                                                                e.target.value || null
                                                            )
                                                        }
                                                        inputProps={{ style: { padding: "6px 8px" } }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>

                <Box style={{ textAlign: "center", marginTop: 8 }}>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        startIcon={<Save />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
